import { useEffect, useLayoutEffect, useState } from "react";
import { App } from "./App";
import { configureApi } from "./api";
import { AuthProvider, TOKEN_KEY, useAuth } from "./lib/auth";
import { resolveBootConfig, type RuntimeConfig } from "./lib/config";
import { SignIn } from "./components/SignIn";
import { InviteFailureBanner } from "./components/InviteFailureBanner";
import { MembersPanel } from "./components/MembersPanel";
import { StudioNav, type StudioView } from "./components/StudioNav";

/** Boot sequence: resolve /config.json, then pick the mode.
 *
 *  - live (local dev, the packaged bundle): same-origin API, no auth — exactly
 *    the pre-hosted behavior.
 *  - static (saga.penseur.ai): Guardius sign-in gates the studio. The API
 *    client points at the snapshot API with a bearer token; what the UI can do
 *    is then driven by the token's capability — a member gets the writable
 *    studio on `/api/studio/*`, a reader keeps the read-only showcase on `/api/*`.
 */
export function Root() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    resolveBootConfig()
      .then((resolved) => {
        if (resolved.mode === "static") {
          // Point the client at the snapshot API + bearer token. The studio
          // prefix is decided later by StudioShell once the token's capability
          // and the chosen source are known.
          configureApi({
            baseUrl: resolved.apiUrl,
            getToken: () => sessionStorage.getItem(TOKEN_KEY),
          });
        }
        setConfig(resolved);
      })
      .catch((error: unknown) => setBootError(String(error)));
  }, []);

  if (bootError) {
    return (
      <div className="signin">
        <div className="signin-card">
          <div className="diagnostic">site configuration failed to load: {bootError}</div>
        </div>
      </div>
    );
  }
  if (!config) return null;
  if (config.mode === "live") return <App readOnly={false} />;
  return (
    <AuthProvider config={config}>
      <Gate config={config} />
    </AuthProvider>
  );
}

/** Requires a token before rendering the studio. Once signed in, hands off to
 *  StudioShell, which derives what the signed-in identity can do from the
 *  token's capability. */
function Gate({ config }: { config: RuntimeConfig }) {
  const { token } = useAuth();
  if (!token) return <SignIn />;
  return (
    <>
      <InviteFailureBanner />
      <StudioShell config={config} />
    </>
  );
}

/** Capability-driven studio for the hosted (static) site. One {@link StudioNav}
 *  view switch drives everything: a member (`capability.canAuthor`) gets the
 *  writable studio routed to `/api/studio/*` and can drop to the read-only
 *  showcase; an owner (`capability.canManageTeam`) additionally gets the Members
 *  admin panel; a reader stays on the showcase (`/api/*`, read-only) with no nav.
 *
 *  Capability is UI-gating only — the snapshot API's Guardius authorizer is the
 *  authoritative enforcement point. `configureApi` is re-applied via
 *  `useLayoutEffect` so the studio prefix is committed before App's mount-time
 *  data effects fire. */
function StudioShell({ config }: { config: RuntimeConfig }) {
  const { capability } = useAuth();
  const [view, setView] = useState<StudioView>(capability.canAuthor ? "studio" : "showcase");
  const studio = view === "studio";

  useLayoutEffect(() => {
    configureApi({
      baseUrl: config.apiUrl,
      getToken: () => sessionStorage.getItem(TOKEN_KEY),
      studio,
    });
  }, [config.apiUrl, studio]);

  // Remount App on a view flip so its prefix-dependent fetches (primitives/history/
  // definitions) re-run against the freshly-configured base. Known Phase 1 limitation:
  // this discards App state, so toggling away from "My studio" loses an unsaved editor
  // draft without warning — a dirty-state confirm is tracked as a Phase 2 follow-up.
  return (
    <>
      <StudioNav
        view={view}
        canAuthor={capability.canAuthor}
        canManageTeam={capability.canManageTeam}
        canInvite={capability.canInvite}
        onChange={setView}
      />
      {view === "members" && (capability.canInvite || capability.canManageTeam) ? (
        <MembersPanel canInvite={capability.canInvite} canManageTeam={capability.canManageTeam} />
      ) : (
        <App key={studio ? "studio" : "showcase"} readOnly={!studio} />
      )}
    </>
  );
}
